import * as XLSX from 'xlsx';
import { ClientReview, UserMap } from '../types';

export interface ExportReviewsParams {
  reviews: ClientReview[];
  userMap?: UserMap;
  startDate?: string;
  endDate?: string;
}

/**
 * Exports reviews to an Excel file with formatted data
 */
export const exportReviewsToExcel = ({
  reviews,
  userMap = {},
  startDate,
  endDate
}: ExportReviewsParams): void => {
  if (!reviews || reviews.length === 0) {
    alert('No hay reseñas para exportar');
    return;
  }

  // Prepare data rows for Excel
  const excelData = reviews.map((review) => {
    const userName = review.user_id && userMap[review.user_id] 
      ? userMap[review.user_id] 
      : `Usuario ${review.user_id || 'N/A'}`;

    return {
      'Fecha': review.timestamp ? new Date(review.timestamp).toLocaleString('es-ES') : 'N/A',
      'Usuario': userName,
      'ID Pedido': review.receipt_id || 'N/A',
      'ID Cliente': review.client_id || 'N/A',
      
      // Sabor ratings and comments
      'Calificación Sabor': review.object?.sabor?.rating ?? 'N/A',
      'Comentario Sabor': review.object?.sabor?.comment || '',
      'Plato Favorito': review.object?.sabor?.favorite?.item_name || '',
      'Peor Plato': review.object?.sabor?.worst?.item_name || '',
      
      // Amabilidad ratings and comments
      'Calificación Amabilidad': review.object?.amabilidad?.rating ?? 'N/A',
      'Comentario Amabilidad': review.object?.amabilidad?.comment || '',
      
      // Experiencia ratings and comments
      'Calificación Experiencia': review.object?.experiencia?.rating ?? 'N/A',
      'Comentario Experiencia': review.object?.experiencia?.comment || '',
      
      // Average rating
      'Promedio': calculateAverageRating(review)
    };
  });

  // Create worksheet
  const worksheet = XLSX.utils.json_to_sheet(excelData);

  // Set column widths for better readability
  const columnWidths = [
    { wch: 20 }, // Fecha
    { wch: 20 }, // Usuario
    { wch: 12 }, // ID Pedido
    { wch: 12 }, // ID Cliente
    { wch: 18 }, // Calificación Sabor
    { wch: 40 }, // Comentario Sabor
    { wch: 25 }, // Plato Favorito
    { wch: 25 }, // Peor Plato
    { wch: 22 }, // Calificación Amabilidad
    { wch: 40 }, // Comentario Amabilidad
    { wch: 24 }, // Calificación Experiencia
    { wch: 40 }, // Comentario Experiencia
    { wch: 12 }  // Promedio
  ];
  worksheet['!cols'] = columnWidths;

  // Create workbook
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, 'Reseñas');

  // Generate filename with date range
  const dateRangeStr = startDate && endDate 
    ? `${startDate}_${endDate}`
    : new Date().toISOString().split('T')[0];
  
  const filename = `Reseñas_${dateRangeStr}.xlsx`;

  // Save file
  XLSX.writeFile(workbook, filename);
};

/**
 * Calculate average rating from a review
 */
const calculateAverageRating = (review: ClientReview): string => {
  const ratings: number[] = [];
  
  if (review.object?.sabor?.rating) {
    ratings.push(review.object.sabor.rating);
  }
  if (review.object?.amabilidad?.rating) {
    ratings.push(review.object.amabilidad.rating);
  }
  if (review.object?.experiencia?.rating) {
    ratings.push(review.object.experiencia.rating);
  }
  
  if (ratings.length === 0) return 'N/A';
  
  const average = ratings.reduce((sum, rating) => sum + rating, 0) / ratings.length;
  return average.toFixed(2);
};
